import React, { memo, useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useHistory } from 'react-router-dom'
import Button from '../Button'
import { ButtonBase } from '@material-ui/core'

import cn from 'classnames'
import styles from './TopNav.module.scss'
import { ReactComponent as OrderSummaryIcon } from '../../assets/icons/order_summary_bars_icon.svg'
import { ReactComponent as InputSearchIcon } from '../../assets/input-search-icon.svg'
import { ReactComponent as CloseIcon } from '../../assets/icons/Close-Icon.svg'

import DropdownSelector from '../DropdownSelector'
import useGetIsMobileScreen from '../../hooks/useGetIsMobileScreen'
import { ORDERS_STATUS_OPTIONS } from '../../utils/orderStates'
import { clearOrderInProgress } from '../../actions/order'
import { useDispatch } from 'react-redux'
import { setSyncWarningDialog } from '../../VNDialogs/ActionCreators'

import { isE700Device } from '../../VNAndroidSDK/Utils'

const TopNav = ({
  currentSearch,
  hasUnsyncedOrders,
  onClearSearch,
  onSearch,
  isSyncInProgress,
  isKiosk,
  filterOrders,
  className,
  tabsModeEnabled,
}) => {
  const ordersStatusOptions = ['My Open Tabs', 'My Closed Orders', 'All Orders']
  const [ordersStatus, setOrdersStatus] = useState(ordersStatusOptions[0])

  const classNames = cn(styles.TopNav, className)
  const isMobile = useGetIsMobileScreen()
  const history = useHistory()
  const [isSearchOpen, setSearchOpen] = useState(false)
  const isMobileViewPort = useGetIsMobileScreen()
  const dispatch = useDispatch()

  /**
   * Always set filter status to default (don't display tabbed orders)
   * when the component is mounted and tabs are disabled
   * Any update to the default filter here also needs to be done in the initial state
   * in /src/stores/initialStates.js
   */
  useEffect(() => {
    if (tabsModeEnabled) {
      filterOrders({ state: 'tabbed', showOrders: ORDERS_STATUS_OPTIONS.OPEN_ORDERS })
      return
    }

    filterOrders({ state: 'tabbed', showOrders: ORDERS_STATUS_OPTIONS.CLOSED_ORDERS })
  }, [tabsModeEnabled, filterOrders])

  useEffect(() => {
    if (!isSearchOpen) {
      onClearSearch()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSearchOpen])

  const useFocus = () => {
    const htmlElRef = useRef(null)
    const setFocus = () => {
      htmlElRef.current && htmlElRef.current.focus()
    }

    return [htmlElRef, setFocus]
  }

  const [inputFocus, setInputFocus] = useFocus() // sets focus to <input> using ref if search icon is clicked

  const onSummaryClick = () => {
    history.push('orders/device-summary')
  }

  const onNewOrderClick = () => {
    const newOrderCallback = () => {
      dispatch(clearOrderInProgress())
      history.push('/concession-order')
    }

    if (isSyncInProgress) {
      dispatch(setSyncWarningDialog(true, newOrderCallback))
      return
    }

    newOrderCallback()
  }

  const onSearchFocus = () => {
    setSearchOpen(true)
  }
  const onSearchBlur = () => {
    if (currentSearch !== '') {
      setSearchOpen(true)
    } else {
      setSearchOpen(false)
    }
  }

  const onClearClick = () => {
    onClearSearch()
    setSearchOpen(false)
  }

  //TODO: get all this styling in a single place
  const customStyling = {
    customAutocompleteStyles: {
      width: isMobileViewPort ? '86.667vw' : '12vw',
      height: isMobileViewPort ? '11.111vw' : '3.8vw',
      paddingTop: 0,
      paddingRight: 0,
    },
    customInputLabelStyles: {
      color: 'white',
      fontSize: isMobileViewPort ? '4.444vw' : '1.042vw',
      paddingBottom: isMobileViewPort ? '1.111vw' : 0,
      marginTop: isMobileViewPort ? '-0.8vh' : 0,
    },
    customTextFieldStyles: {
      height: isMobileViewPort ? '11.111vw' : '3.78vw',
      fontSize: isMobileViewPort ? '4.444vw' : '1.042vw',
      color: 'white',
      borderRadius: isMobileViewPort ? '0.6rem' : 6,
      paddingLeft: 5,
      paddingRight: 15,
      textAlign: 'center',
      cursor: 'pointer',
    },
    customPaperStyles: {
      maxHeight: 'auto',
      backgroundColor: '#383B3D',
      fontSize: isMobileViewPort ? '4.444vw' : '1.042vw',
    },
  }

  const handleFilterOrders = (inputValue) => {
    setOrdersStatus(inputValue)

    if (inputValue === 'My Open Tabs') {
      return filterOrders({ showOrders: ORDERS_STATUS_OPTIONS.OPEN_ORDERS })
    }

    if (inputValue === 'My Closed Orders') {
      return filterOrders({ showOrders: ORDERS_STATUS_OPTIONS.CLOSED_ORDERS })
    }

    if (inputValue === 'All Orders') {
      return filterOrders({ showOrders: ORDERS_STATUS_OPTIONS.ALL_ORDERS })
    }
  }

  if (isMobile) {
    return (
      <div className={classNames}>
        <div className={styles.row}>
          <div className={cn(styles.barsContainer, { [styles.hidden]: isSearchOpen })}>
            <ButtonBase
              disableRipple={isE700Device()}
              component="div"
              className={cn(styles.bars, styles.mobile, { [styles.unsynced]: hasUnsyncedOrders })}
              onClick={onSummaryClick}
            >
              <OrderSummaryIcon />
            </ButtonBase>
          </div>
          <ButtonBase disableRipple={isE700Device()} component="div" className={styles.fieldContainer}>
            <div className={isSearchOpen ? styles.searchIconContainer : styles.searchCloseContainer}>
              <InputSearchIcon className={styles.searchIcon} onClick={setInputFocus} />
            </div>
            <input
              ref={inputFocus}
              id={'search-order-input'}
              type="text"
              className={cn({ [styles.searchOpen]: isSearchOpen })}
              value={currentSearch}
              isSearchOpen={isSearchOpen}
              onFocus={onSearchFocus}
              onBlur={onSearchBlur}
              onChange={(e) => onSearch(e.target.value)}
            />
            {currentSearch && (
              <div className={styles.clearIconContainer} onClick={onClearClick}>
                <div className={styles.clearButton}>
                  <CloseIcon />
                </div>
              </div>
            )}
          </ButtonBase>
        </div>
        <div className={cn([styles.mobileOrdersFilter, !tabsModeEnabled && styles.mobileOrdersFilterNoTabs])}>
          {tabsModeEnabled && (
            <DropdownSelector
              value={ordersStatus}
              autocompleteOptions={ordersStatusOptions}
              onAutocompleteChange={(inputValue) => {
                handleFilterOrders(inputValue)
              }}
              {...customStyling}
            />
          )}
        </div>
      </div>
    )
  }

  return (
    <div className={classNames}>
      <>
        <div className={styles.row}>
          <div className={styles.col}>
            {!isKiosk && tabsModeEnabled && (
              <div className={styles.ordersFilter}>
                <DropdownSelector
                  value={ordersStatus}
                  autocompleteOptions={ordersStatusOptions}
                  onAutocompleteChange={(inputValue) => {
                    handleFilterOrders(inputValue)
                  }}
                  {...customStyling}
                />
              </div>
            )}
          </div>

          <div className={styles.col}>
            <ButtonBase
              disableRipple={isE700Device()}
              component="div"
              className={cn(styles.bars, styles.tablet, { [styles.unsynced]: hasUnsyncedOrders })}
              onClick={onSummaryClick}
            >
              <OrderSummaryIcon className={styles.summaryIcon} />
            </ButtonBase>
          </div>

          <div className={styles.col}>
            {!isKiosk && (
              <Button
                useMui
                className={cn([styles.newOrderBtn, !tabsModeEnabled && styles.newOrderBtnNoTabs])}
                onClick={onNewOrderClick}
              >
                New Order
              </Button>
            )}
          </div>
        </div>
      </>
    </div>
  )
}

TopNav.defaultTypes = {
  currentSearch: '',
}

TopNav.propTypes = {
  currentSearch: PropTypes.string,
  onClearSearch: PropTypes.func,
  onSearch: PropTypes.func,
  filterOrder: PropTypes.func,
}

export default memo(TopNav)
